import IconCopy from '@apollo/icons/default/IconCopy.svg';
import IconRun from '@apollo/icons/default/IconRun.svg';
import {
  Alert,
  ButtonGroup,
  Group,
  IconButton,
  Link,
  Spinner,
  Text,
} from '@apollo/orbit';
import copy from 'copy-to-clipboard';
import React from 'react';
import { Link as ReactRouterLink } from 'react-router-dom';

import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { TooltipWithMutableContent } from 'src/components/TooltipWithMutableContent';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { Code } from 'src/lib/safeCompileMarkdown/Code';

import { useInternalGraphLinking } from '../../hooks/useInternalGraphLinking';

export const OperationCollectionEntryMarkdownElement = ({
  operation,
  loading,
}: {
  operation:
    | GraphQLTypes.OperationCollectionEntriesReadmeQuery_operationCollectionEntries
    | undefined;
  loading: boolean;
}) => {
  const { locationToExplorer } = useInternalGraphLinking();

  if (loading) {
    return (
      <Group gap={2} className="my-4 rounded border border-primary p-3">
        <Spinner size="xs" />
        <Text as="span" variant="bodySb">
          Loading
        </Text>
      </Group>
    );
  }

  if (!operation) {
    return (
      <Alert className="my-4" status="warning">
        Saved operation not found
      </Alert>
    );
  }

  return (
    <div className="my-4 overflow-hidden rounded border border-primary">
      <div className="flex items-center justify-between border-b border-b-primary p-4">
        <Link
          title={`${operation.collection.name} / ${operation.name}`}
          className="truncate"
          to={locationToExplorer({
            savedCollectionId: operation.collection.id,
            savedCollectionEntryId: operation.id,
          })}
        >
          {operation.collection.name} / {operation.name}
        </Link>
        <ButtonGroup>
          <TooltipWithMutableContent
            initialContent="Copy codeblock"
            placement="top"
          >
            {({ setContent, resetContent }) => (
              <IconButton
                onClick={() => {
                  copy(operation?.currentOperationRevision.body);
                  setContent('Copied');
                }}
                size="sm"
                variant="hidden"
                onMouseEnter={() => resetContent()}
                data-analytics-label="README content: Copy codeblock"
                data-analytics-category="Variant Home"
                aria-label="Copy codeblock"
                icon={<IconCopy />}
              />
            )}
          </TooltipWithMutableContent>
          <Tooltip label="Open saved operation in Explorer" placement="top">
            <IconButton
              size="sm"
              variant="primary"
              icon={<IconRun />}
              as={ReactRouterLink}
              to={locationToExplorer({
                savedCollectionId: operation.collection.id,
                savedCollectionEntryId: operation.id,
              })}
              data-analytics-label="README content: Open saved operation in Explorer"
              data-analytics-category="Variant Home"
              aria-label="Open saved operation in Explorer"
            />
          </Tooltip>
        </ButtonGroup>
      </div>

      {/* TODO: Extract the inner code block to a shared component and use that directly */}
      <div className="[&>div]:m-0 [&>div]:rounded-none [&>div]:border-none">
        <Code
          inline={false}
          hideActionButtons={true}
          // eslint-disable-next-line tailwindcss/no-custom-classname
          className="lang-graphql"
        >
          {operation.currentOperationRevision.body}
        </Code>
      </div>
    </div>
  );
};
